import {useEffect, useState} from "react";
import {createPortal} from "react-dom";

import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import StyledSwitch from "@plannel/components/ui/StyledSwitch";
import VersionAutocomplete from "@plannel/components/filter/VersionAutocomplete";
import GavelIcon from '@mui/icons-material/Gavel';
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import ArrowRightIcon from "@mui/icons-material/ArrowForward";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";

import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { green, grey } from "@mui/material/colors";

import { useTranslation, withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {includes, isEmpty, isEqual, isUndefined, sortBy, values} from "lodash";
import { isNull } from "lodash/lang";
import { addDays } from "date-fns";

import CodeUtil from "@plannel/pages/utils/code-util";
import reduxUtil from "@plannel/utils/redux-util";
import dateUtils from "@plannel/utils/date-util";
import { getDefaultRangeByBucketUnit, calcPlanEndDate } from "@plannel/pages/master-plan/utils/mp-bucket-util";
import RpPeriodSlider from "@plannel/components/RpPeriodSlider";
import PlanStrategyHelp from "@plannel/components/PlanStrategyHelp";
import { MpBucketStrategySettings } from "@plannel/components/ModuleVersionSetting";
import { initializeTimeBuckets } from "@plannel/pages/master-plan/utils/mp-bucket-util";
import DemandPriorityList from "@plannel/components/DemandPriorityList";

const SALES = "SALES";
const DEMAND = "DEMAND";
const ROLE_APP_DP = "ROLE_APP_DP";
const ROLE_APP_RP = "ROLE_APP_RP";

const {
  buckets, versionStatusCode, dayOfWeek, module, rpDemandDistributionPolicy, planStrategies, datePriorityNameByCode, itemType,
  mpPlanConfig: {
    BACKORDER, MC, RC, PS, DEMAND_PRIORITY, DATE_PRIORITY,
    SAFETY_STOCK_POLICY, KIT_PLAN_MODE, ALLOW_SEMI_ORDER,
    FROZEN_RANGE, ALT_RES_POLICY
  },
  priorityItems
} = CodeUtil;
const { RELEASED } = versionStatusCode;
const { DAY, PARTIAL_WEEK, WEEK, MONTH } = buckets;
const { RP, MP, DP } = module;
const { EQUAL_BY_BUCKET, LAST_OF_BUCKET } = rpDemandDistributionPolicy;
const { PRODUCTION_PRIORITY } = priorityItems;

const constraints = [{ name: "finite", code: "F" }, { name: "infinite", code: "INF" }];
const materialConstraints = [...constraints, { name: "masterData", code: "MD" }];
const allowBackorder = [{ name: "allow", code: "true" }, { name: "notAllow", code: "false" }];
const allowImmediateShipment = [{ name: "shipmentSame", code: "true" }, { name: "shipmentNext", code: "false" }];
const safetyStockPolicyOptions = [
  { key: "key_none", code: "NONE", name: "notUse" },
  { key: "key_targetStock", code: "TargetStock", name: "targetStock" },
  { key: "key_minMax", code: "MinMax", name: "minMax" },
];
const kitPlanModeOptions = [{ key: "key_kit", code: true, name: itemType.KIT.name }, { key: "key_fg", code: false, name: itemType.FG.name }];
const allowSemiOrderOptions = [{ key: "key_allow", code: true, name: "allow" }, { key: "key_notAllow", code: false, name: "notAllow" }];
const altResPolicyOptions = [
  { key: "key_fast", code: PRODUCTION_PRIORITY.FAST, name: PRODUCTION_PRIORITY.FAST },
  { key: "key_priority", code: PRODUCTION_PRIORITY.PRIORITY, name: PRODUCTION_PRIORITY.PRIORITY },
  { key: "key_masterData", code: PRODUCTION_PRIORITY.MASTER_DATA, name: PRODUCTION_PRIORITY.MASTER_DATA }
];
// forward2 는 선택지에 내지 않는다 — 포워드 선택 시 "계획 기준"(가용 시점 기준=forward / 납기 기준=forward2)으로 고른다.
const planScenarioItems = Object.values(planStrategies)
  .filter(strategy => strategy !== planStrategies.forWard2)
  .map(strategy => ({ name: strategy, code: strategy }));

const fieldSetStyle = { borderColor: "#eeeeee", borderStyle: "solid", borderRadius: "2%" };
const MP_POLICY_LABEL_SX = { mb: 0.5, minWidth: 220, maxWidth: 220 };
const MP_POLICY_CONTROL_WIDTH = 200;
const RP_POLICY_LABEL_SX = {
  minWidth: { xs: 150, sm: 190, md: 240 },
  maxWidth: { xs: 150, sm: 190, md: 240 },
  mr: 2,
  flexShrink: 0,
};
const RP_POLICY_CONTROL_SX = {
  flex: 1,
  minWidth: 0,
  maxWidth: 220,
};
const VersionConfigContent = ({ t, viewName, moduleCd, defaultValue, selectedPlanVersion, dpVersions, rpVersions, configObj, useModules, planPolicyContainer, rpPolicyContainer, fitContent = false }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const startDay = dayOfWeek.MONDAY.cd;


  const getBucketUnit = obj => {
    if (obj?.BUCKET_UNIT) return obj.BUCKET_UNIT;
    if (isEqual(moduleCd, MP) && Array.isArray(obj?.TIME_BUCKETS) && obj.TIME_BUCKETS.length > 0) {
      return obj.TIME_BUCKETS[0].UNIT;
    }
    return (defaultValue.bucket ?? "");
  }

  const getPlanningRange = obj => {
    if (obj?.PLANNING_RANGE) return obj.PLANNING_RANGE;
    if (isEqual(moduleCd, MP) && Array.isArray(obj?.TIME_BUCKETS) && obj.TIME_BUCKETS.length > 0) {
      return obj.TIME_BUCKETS.reduce((sum, tb) => sum + (tb.COUNT || 0), 0);
    }
    return (defaultValue.range ?? "");
  }

  // 시작일/종료일은 반드시 복사해서 넘긴다 — onChangePeriod·onChangePlanningRange·getWeekStartDate 가
  // Date 를 setDate 로 제자리 변경하므로, defaultValue.start 를 그대로 주면 호출자의 기본값 객체
  // (RP Settings 로 만든 rpSettingValue)가 오염돼 다음 모달까지 끌고 간다.
  const getPlanStartDate = obj => {
    if (obj?.PLAN_START_DATE) {
      return new ZDate(obj.PLAN_START_DATE);
    }

    return defaultValue.start ? new ZDate(defaultValue.start) : null;
  }

  const getPlanEndDate = obj => {
    if (obj?.PLAN_END_DATE) {
      return new ZDate(obj.PLAN_END_DATE);
    }

    if (defaultValue.end) {
      return new ZDate(defaultValue.end);
    }

    if (defaultValue.start && defaultValue.range !== undefined && defaultValue.range !== null) {
      return addDays(defaultValue.start, defaultValue.range);
    }

    return null;
  }

  const getDemandDistributionPolicy = obj => {
    return obj?.DEMAND_DISTRIBUTION_POLICY ? obj.DEMAND_DISTRIBUTION_POLICY : defaultValue.demandDistributionPolicy;
  }

  const getSalesPeriodRef = obj => {
    if (Object.prototype.hasOwnProperty.call(obj, "SALES_REF_PERIOD")) { // 기존에 생성 된 version 처리
      const salesRef = Object.prototype.hasOwnProperty.call(obj, "SALES_REF_PERIOD") ? obj.SALES_REF_PERIOD : defaultValue.salesRefStart;

      return [salesRef, 0];
    } else {
      const salesRefStart = Object.prototype.hasOwnProperty.call(obj, "SALES_REF_START") ? obj.SALES_REF_START : defaultValue.salesRefStart;
      const salesRefEnd = Object.prototype.hasOwnProperty.call(obj, "SALES_REF_END") ? obj.SALES_REF_END : defaultValue.salesRefEnd;

      return [salesRefStart, salesRefEnd];
    }
  }

  const getDemandPeriodRef = obj => {
    if (Object.prototype.hasOwnProperty.call(obj, "DEMAND_REF_PERIOD")) { // 기존에 생성 된 version 처리
      const demandRef = Object.prototype.hasOwnProperty.call(obj, "DEMAND_REF_PERIOD") ? obj.DEMAND_REF_PERIOD : defaultValue.demandRefEndDate;

      return [0, demandRef];
    } else {
      const demandRefStartDate = Object.prototype.hasOwnProperty.call(obj, "DEMAND_REF_START") ? obj.DEMAND_REF_START : defaultValue.demandRefStartDate;
      const demandRefEndDate = Object.prototype.hasOwnProperty.call(obj, "DEMAND_REF_END") ? obj.DEMAND_REF_END : defaultValue.demandRefEndDate;

      return [demandRefStartDate, demandRefEndDate];
    }
  }

  const getAllowImmediateShipment = obj => {
    if (!obj?.IMMEDIATE_SHIPMENT) {
      return "shipmentSame";
    }

    if (obj.IMMEDIATE_SHIPMENT === "allow") {
      return "shipmentSame";
    } else if (obj.IMMEDIATE_SHIPMENT === "notAllow") {
      return "shipmentNext";
    }

    return obj?.IMMEDIATE_SHIPMENT ? obj.IMMEDIATE_SHIPMENT : defaultValue.immediateShipment;
  }

  // boolean 은 ?: 로 폴백하면 안 된다 — 버전에 명시적으로 false 가 저장돼 있어도 falsy 라서
  // defaultValue(RP Settings)로 덮여, 사용자가 끈 설정이 다시 켜진 채로 보인다. ?? 로 "키 없음"만 폴백한다.
  const getKitDemandMergeFlg = obj => {
    return obj?.KIT_DEMAND_MERGE_FLG ?? defaultValue.kitDemandMergeFlg;
  }
  const getUseTargetSupplyDays = obj => {
    return obj?.USE_TARGET_SUPPLY_DAYS ?? defaultValue.useTargetSupplyDays;
  }

  const getNoteFlg = obj => {
    return obj?.NOTE_FLG ?? defaultValue.noteFlg;
  }

  const getMultiSourcingFlg = obj => {
    return obj?.MULTI_SOURCING_FLG ?? defaultValue.multiSourcingFlg;
  }

  const getEnableRollingPlan = obj => {
    return obj?.ENABLE_ROLLING_PLAN ?? defaultValue.enableRollingPlan ?? false;
  }

  const getObjectByKey = (obj, key) => {

    if (obj && Object.prototype.hasOwnProperty.call(obj, key)) {
      return obj?.[key];
    } else if (isEqual(key, MC.code)) {
      return defaultValue[MC.name];
    } else if (isEqual(key, PS.code)) {
      return defaultValue[PS.name];
    } else if (isEqual(key, RC.code)) {
      return defaultValue[RC.name];
    } else if (isEqual(key, BACKORDER.code)) {
      return defaultValue[BACKORDER.name];
    } else if (isEqual(key, DEMAND_PRIORITY.code)) {
      return defaultValue[DEMAND_PRIORITY.name];
    } else if (isEqual(key, DATE_PRIORITY.code)) {
      return defaultValue[DATE_PRIORITY.name];
    } else if (isEqual(key, SAFETY_STOCK_POLICY.code)) {
      return defaultValue[SAFETY_STOCK_POLICY.code] ?? "NONE";
    } else if (isEqual(key, KIT_PLAN_MODE.code)) {
      return defaultValue[KIT_PLAN_MODE.code];
    } else if (isEqual(key, ALLOW_SEMI_ORDER.code)) {
      return defaultValue[ALLOW_SEMI_ORDER.code];
    } else if (isEqual(key, FROZEN_RANGE.code)) {
      return defaultValue?.[FROZEN_RANGE.name] ?? 0;
    } else if (isEqual(key, ALT_RES_POLICY.code)) {
      return defaultValue[ALT_RES_POLICY.name];
    }

    return null;
  }

  const getDpVersion = obj => {
    if (obj?.DP_VERSION_ID) {
      const versionId = obj.DP_VERSION_ID;
      if (!isEmpty(dpVersions)) {
        for (const version of dpVersions) {
          if (version.id === versionId) {
            return version;
          }
        }
      }
    }

    return null;
  }

  const getRpVersion = obj => {
    if (obj?.RP_VERSION_ID) {
      const versionId = obj.RP_VERSION_ID;
      if (!isEmpty(rpVersions)) {
        for (const version of rpVersions) {
          if (version.id === versionId) {
            return version;
          }
        }
      }
    }

    return null;
  }

  const [isLoading, setIsLoading] = useState(false);
  const [bucketUnit, setBucketUnit] = useState(getBucketUnit(configObj.current));
  const [planningRange, setPlanningRange] = useState(getPlanningRange(configObj.current));
  const getRawInitTimeBuckets = (obj) => {
    if (isEqual(moduleCd, MP) && Array.isArray(obj?.TIME_BUCKETS) && obj.TIME_BUCKETS.length > 0) {
      return obj.TIME_BUCKETS;
    }
    return [{ UNIT: getBucketUnit(obj) || DAY, COUNT: getPlanningRange(obj) || defaultValue.range || 90 }];
  };

  const [timeBuckets, setTimeBuckets] = useState(() => {
    const rawTbs = getRawInitTimeBuckets(configObj.current);
    const rawPlanStart = getPlanStartDate(configObj.current);
    return initializeTimeBuckets(rawPlanStart, rawTbs).newTbs;
  });
  const [planStartDate, setPlanStartDate] = useState(() => {
    const rawTbs = getRawInitTimeBuckets(configObj.current);
    const rawPlanStart = getPlanStartDate(configObj.current);
    return initializeTimeBuckets(rawPlanStart, rawTbs).newPlanStart;
  });
  const [planEndDate, setPlanEndDate] = useState(getPlanEndDate(configObj.current));

  const [salesPeriodRef, setSalesPeriodRef] = useState(getSalesPeriodRef(configObj.current));
  const [demandPeriodRef, setDemandPeriodRef] = useState(getDemandPeriodRef(configObj.current));
  const [demandDistributionPolicy, setDemandDistributionPolicy] = useState(getDemandDistributionPolicy(configObj.current));
  const [dpVersion, setDpVersion] = useState(getDpVersion(configObj.current));
  const [rpVersion, setRpVersion] = useState(getRpVersion(configObj.current));
  const [sourceRpVersion, setSourceRpVersion] = useState(null);
  const [rpVersionsState, setRpVersionsState] = useState([]);
  const [planStartWeek, setPlanStartWeek] = useState('');
  const [planEndWeek, setPlanEndWeek] = useState('');

  const [kitDemandMergeFlg, setKitDemandMergeFlg] = useState(getKitDemandMergeFlg(configObj.current));
  const [noteFlg, setNoteFlg] = useState(getNoteFlg(configObj.current));
  const [multiSourcingFlg, setMultiSourcingFlg] = useState(getMultiSourcingFlg(configObj.current));
  const [immediateShipment, setImmediateShipment] = useState(getAllowImmediateShipment(configObj.current));
  const [initialDpVersion, setInitialDpVersion] = useState(getDpVersion(configObj.current));
  const [resourceCapacity, setResourceCapacity] = useState(getObjectByKey(configObj.current, RC.code));
  const [materialConst, setMaterialConst] = useState(getObjectByKey(configObj.current, MC.code));
  const [planStrategy, setPlanStrategy] = useState(getObjectByKey(configObj.current, PS.code));
  const [backorder, setBackorder] = useState(getObjectByKey(configObj.current, BACKORDER.code));
  const [safetyStockPolicy, setSafetyStockPolicy] = useState(getObjectByKey(configObj.current, SAFETY_STOCK_POLICY.code) ?? "NONE");
  const [kitPlanMode, setKitPlanMode] = useState(getObjectByKey(configObj.current, KIT_PLAN_MODE.code));
  const [allowSemiOrder, setAllowSemiOrder] = useState(getObjectByKey(configObj.current, ALLOW_SEMI_ORDER.code));
  const [frozenRange, setFrozenRange] = useState(getObjectByKey(configObj.current, FROZEN_RANGE.code));
  const [demandPriority, setDemandPriority] = useState(getObjectByKey(configObj.current, DEMAND_PRIORITY.code));
  const [datePriority, setDatePriority] = useState(getObjectByKey(configObj.current, DATE_PRIORITY.code));
  const [useTargetSupplyDays, setUseTargetSupplyDays] =  useState(getUseTargetSupplyDays(configObj.current));
  const [altResPolicy, setAltResPolicy] = useState(getObjectByKey(configObj.current, ALT_RES_POLICY.code));
  const [enableRollingPlan, setEnableRollingPlan] = useState(getEnableRollingPlan(configObj.current));

  const firstPlanBucketUnit = isEqual(moduleCd, MP)
    ? (timeBuckets[0]?.UNIT ?? bucketUnit)
    : bucketUnit;

  const lastPlanBucketUnit = isEqual(moduleCd, MP)
    ? (timeBuckets[timeBuckets.length - 1]?.UNIT ?? bucketUnit)
    : bucketUnit;

  const mpFirstBucketSize = isEqual(moduleCd, MP) ? Math.max(1, timeBuckets[0]?.COUNT ?? 1) : 0;

  const isStartMonthBucket = isEqual(firstPlanBucketUnit, MONTH);
  const isStartWeekBucket = isEqual(firstPlanBucketUnit, WEEK) || isEqual(firstPlanBucketUnit, PARTIAL_WEEK);
  const isEndMonthBucket = isEqual(lastPlanBucketUnit, MONTH);
  const isEndWeekBucket = isEqual(lastPlanBucketUnit, WEEK) || isEqual(lastPlanBucketUnit, PARTIAL_WEEK);

  const onChangeSliderValueHandler = (_, newValue, type) => {
    if (isEqual(type, SALES)) {
      setSalesPeriodRef([newValue[0], newValue[1]]);
    } else if (isEqual(type, DEMAND)) {
      setDemandPeriodRef([newValue[0], newValue[1]]);
    }
  };

  const onChangeSelectValueHandler = (newValue, moduleType) => {
    if (isEqual(moduleType, DP)) {
      setDpVersion(newValue);
    } else if (isEqual(moduleType, RP)) {
      setRpVersion(newValue);
    }
  }

  const handleChange = (event) => {
    const { target: { value, name } } = event;
    if (isEqual(name, RC.name)) {
      setResourceCapacity(value);
    } else if (isEqual(name, MC.name)) {
      setMaterialConst(value);
    } else if (isEqual(name, PS.name)) {
      setPlanStrategy(value);
    } else if (isEqual(name, BACKORDER.name)) {
      setBackorder(value);
    } else if (isEqual(name, SAFETY_STOCK_POLICY.name)) {
      setSafetyStockPolicy(value);
    } else if (isEqual(name, KIT_PLAN_MODE.name)) {
      setKitPlanMode(value);
      if (value) {
        setAllowSemiOrder(false);
      }
    } else if (isEqual(name, ALLOW_SEMI_ORDER.name)) {
      setAllowSemiOrder(value);
    } else if (isEqual(name, FROZEN_RANGE.name)) {
      setFrozenRange(value);
    } else if (isEqual(name, ALT_RES_POLICY.name)) {
      setAltResPolicy(value);
    }
  };

  const getCalculatedPlanEndDate = (startDate, range, unit, tbs) => {
    return calcPlanEndDate(startDate, range, unit, tbs ?? timeBuckets, moduleCd, startDay);
  };

  const onChangePlanningRange = (e) => {
    const name = e.target.name;
    const value = e.target.value;

    if (isEqual(name, "planningRange")) {
      if (isEqual(moduleCd, MP)) {
        // planningRange for MP is derived from timeBuckets; not editable here
        return;
      }
      if (isNull(value) || isUndefined(value) || isNaN(value) || isEqual(value, "") || value < 0) {
        setPlanningRange("");
      } else {
        const rangeVal = Number(value);
        setPlanningRange(rangeVal);
        const endDate = getCalculatedPlanEndDate(planStartDate, rangeVal, bucketUnit, null);
        setPlanEndDate(endDate);
      }
    } else if (isEqual(name, "bucketUnit")) {
      const newBucket = value;
      const newRange = getDefaultRangeByBucketUnit(value) ?? planningRange;

      setBucketUnit(newBucket);
      setPlanningRange(newRange);

      const start = planStartDate;
      if (isEqual(value, MONTH)) {
        start.setDate(1);
        setPlanStartDate(start);
      }

      const endDate = getCalculatedPlanEndDate(start, newRange, value, null);
      setPlanEndDate(endDate);
    } else if (isEqual(name, "frozenRange")) {
      const parseIntValue = parseInt(value, 10);
      if (isNaN(parseIntValue) || isEqual(value, "") || parseIntValue < 0) {
        return;
      }
      if (isEqual(moduleCd, MP)) {
        setFrozenRange(Math.min(parseIntValue, mpFirstBucketSize));
      } else {
        setFrozenRange(parseIntValue);
      }
    }
  }

  const onChangeBucketUnit = (e) => {
    const value = e.target.value;
    setBucketUnit(value);

    onChangePlanningRange({ target: { name: "bucketUnit", value } });
  }

  const onChangeDistributionPolicy = (e) => {
    const value = e.target.value;
    setDemandDistributionPolicy(value);
  }

  const ableDayOfWeek = e => {
    if (isStartWeekBucket) {
      return e.getDay() !== 1;
    }

    return false;
  };

  const getWeekStartDate = (curDate) => {
    const weekDay = values(dayOfWeek).filter(week => isEqual(week.cd, startDay))[0];
    const day = curDate.getDay();

    const convertFlg = ableDayOfWeek(curDate);
    const computeDays = Number(weekDay.id);

    if (convertFlg) {
      const convertDate = Number(curDate.getDate() - day + computeDays);
      const monthAllDays = new ZDate(curDate.getFullYear(), curDate.getMonth() + 1, 0).getDate();
      if (convertDate > monthAllDays) {
        return new ZDate(curDate.getFullYear(), curDate.getMonth() + 1, convertDate - monthAllDays);
      }
      return new ZDate(curDate.setDate(convertDate));
    }

    return curDate;
  };

  const onChangePeriod = (newValue, identifier) => {
    if (identifier === "planStartDate") {
      if (isStartMonthBucket) {
        newValue.setDate(1);
      }

      if ((isEqual(moduleCd, RP) || isEqual(moduleCd, MP)) && isStartWeekBucket) {
        const startDate = getWeekStartDate(newValue);
        newValue.setDate(startDate.getDate());
      }

      setPlanStartDate(newValue);

      const endDate = getCalculatedPlanEndDate(newValue, planningRange, bucketUnit, timeBuckets)
      setPlanEndDate(endDate);
    }
  }

  // 수요 우선순위 재정렬(드래그) — 날짜 항목은 seq 1 고정, 나머지는 순서대로 2,3,...
  const handleReorderDemandPriority = (newOrder) => {
    const dateNames = values(datePriorityNameByCode);
    let priSeq = 2;
    const reseq = newOrder.map(row => {
      if (includes(dateNames, row?.priorityItemName)) {
        return { ...row, seq: 1 };
      }
      const next = { ...row, seq: priSeq };
      priSeq++;
      return next;
    });
    setDemandPriority(sortBy(reseq, "seq"));
  };
  // makePlanConfigObj 변경 시 CreateVersionModal 도 변경해주어야 함
  const makePlanConfigObj = props => {
    const {
      planStartDate, planEndDate, bucketUnit, planningRange, salesPeriodRef, demandPeriodRef, dpVersion, rpVersion, kitDemandMergeFlg, noteFlg, multiSourcingFlg, initialDpVersion,
      backorder, frozenRange, planStrategy, materialConst, resourceCapacity, immediateShipment, demandPriority, useTargetSupplyDays, safetyStockPolicy,
      kitPlanMode, allowSemiOrder, enableRollingPlan
    } = props;

    const now = new ZDate();
    const startDate = planStartDate ? planStartDate : now;
    const endDate = planEndDate ? planEndDate : addDays(startDate, defaultValue.range);
    const dpVersionId = dpVersion ? dpVersion.id : null;
    const rpVersionId = rpVersion ? rpVersion.id : null;

    const planConfigObj = {
      BUCKET_UNIT: bucketUnit ?? defaultValue.bucket,
      PLANNING_RANGE: planningRange ?? defaultValue.range,
      PLAN_START_DATE: dateUtils.formatDate(startDate, "yyyy-MM-dd"),
      PLAN_END_DATE: dateUtils.formatDate(endDate, "yyyy-MM-dd"),
      DP_VERSION_ID: dpVersionId,
      RP_VERSION_ID: rpVersionId
    };

    if (isEqual(moduleCd, RP)) {
      planConfigObj.SALES_REF_START = salesPeriodRef ? salesPeriodRef[0] : defaultValue.salesRefStart;
      planConfigObj.SALES_REF_END = salesPeriodRef ? salesPeriodRef[1] : defaultValue.salesRefEnd;
      planConfigObj.DEMAND_REF_START = demandPeriodRef ? demandPeriodRef[0] : defaultValue.demandRefStartDate;
      planConfigObj.DEMAND_REF_END = demandPeriodRef ? demandPeriodRef[1] : defaultValue.demandRefEndDate;
      planConfigObj.KIT_DEMAND_MERGE_FLG = kitDemandMergeFlg;
      planConfigObj.NOTE_FLG = noteFlg;
      planConfigObj.MULTI_SOURCING_FLG = multiSourcingFlg;
      planConfigObj.DEMAND_DISTRIBUTION_POLICY = demandDistributionPolicy;
      planConfigObj.OLD_DP_VERSION = initialDpVersion?.id;
      planConfigObj.BACKORDER = backorder ? backorder : defaultValue.backorder;
      planConfigObj.IMMEDIATE_SHIPMENT = immediateShipment ? immediateShipment : defaultValue.immediateShipment;
      planConfigObj.USE_TARGET_SUPPLY_DAYS = useTargetSupplyDays;
    } else if (isEqual(moduleCd, MP)) {
      const normalizedFrozenRange = Math.min(Math.max(Number(frozenRange) || 0, 0), mpFirstBucketSize);

      planConfigObj.MC = materialConst ?? defaultValue[MC.name];
      planConfigObj.RC = resourceCapacity ?? defaultValue[RC.name];
      planConfigObj.PS = planStrategy ?? defaultValue[PS.name];
      planConfigObj.BACKORDER = backorder ?? defaultValue[BACKORDER.name];
      planConfigObj.FROZEN_RANGE = normalizedFrozenRange;
      planConfigObj.DEMAND_PRIORITY = demandPriority ?? defaultValue.demandPriority;
      planConfigObj.DATE_PRIORITY = datePriority ?? defaultValue.datePriority;
      planConfigObj.SAFETY_STOCK_POLICY = safetyStockPolicy ?? "NONE";
      planConfigObj.KIT_PLAN_MODE = kitPlanMode ?? defaultValue[KIT_PLAN_MODE.name];
      planConfigObj.ALLOW_SEMI_ORDER = allowSemiOrder ?? defaultValue[ALLOW_SEMI_ORDER.name];
      planConfigObj.ALT_RES_POLICY = altResPolicy ?? defaultValue[ALT_RES_POLICY.name];
      planConfigObj.ENABLE_ROLLING_PLAN = enableRollingPlan ?? defaultValue.enableRollingPlan ?? false;
      planConfigObj.TIME_BUCKETS = timeBuckets;
      planConfigObj.PLANNING_RANGE = timeBuckets.reduce((s, tb) => s + (tb.COUNT || 0), 0);
      delete planConfigObj.BUCKET_UNIT;
    }

    return planConfigObj;
  }

  useEffect(() => {
    const configAppeared = !reduxViewState?.sourceVersion?.planConfigObj && !!selectedPlanVersion?.planConfigObj;
    if (!reduxViewState?.sourceVersion || !isEqual(reduxViewState.sourceVersion.id, selectedPlanVersion?.id) || configAppeared) {
      if (selectedPlanVersion?.planConfigObj) {
        const { planConfigObj } = selectedPlanVersion;
        setBucketUnit(getBucketUnit(planConfigObj));
        setPlanningRange(getPlanningRange(planConfigObj));
        if (isEqual(moduleCd, MP)) {
          const rawTbs = getRawInitTimeBuckets(planConfigObj);
          const rawPlanStart = getPlanStartDate(planConfigObj);
          const { newTbs, newPlanStart } = initializeTimeBuckets(rawPlanStart, rawTbs);
          setTimeBuckets(newTbs);
          setPlanStartDate(newPlanStart);
        } else {
          setPlanStartDate(getPlanStartDate(planConfigObj));
        }
        setPlanEndDate(getPlanEndDate(planConfigObj));

        setSalesPeriodRef(getSalesPeriodRef(planConfigObj));
        setDemandPeriodRef(getDemandPeriodRef(planConfigObj));
        setSourceRpVersion(getRpVersion(planConfigObj));
        setDpVersion(getDpVersion(planConfigObj));
        setKitDemandMergeFlg(getKitDemandMergeFlg(planConfigObj));
        setNoteFlg(getNoteFlg(planConfigObj));
        setMultiSourcingFlg(getMultiSourcingFlg(planConfigObj));
        setDemandDistributionPolicy(getDemandDistributionPolicy(planConfigObj));
        setBackorder(getObjectByKey(planConfigObj, BACKORDER.code));
        setFrozenRange(planConfigObj?.FROZEN_RANGE ? getObjectByKey(planConfigObj, FROZEN_RANGE.code) : 0);
        setResourceCapacity(getObjectByKey(planConfigObj, RC.code));
        setMaterialConst(getObjectByKey(planConfigObj, MC.code));
        setPlanStrategy(getObjectByKey(planConfigObj, PS.code));
        setDemandPriority(getObjectByKey(planConfigObj, DEMAND_PRIORITY.code));
        setDatePriority(getObjectByKey(planConfigObj, DATE_PRIORITY.code));
        setImmediateShipment(getAllowImmediateShipment(planConfigObj));
        setUseTargetSupplyDays(getUseTargetSupplyDays(planConfigObj));
        setSafetyStockPolicy(getObjectByKey(planConfigObj, SAFETY_STOCK_POLICY.code) ?? "NONE");
        setKitPlanMode(getObjectByKey(planConfigObj, KIT_PLAN_MODE.code));
        setAllowSemiOrder(getObjectByKey(planConfigObj, ALLOW_SEMI_ORDER.code));
        setAltResPolicy(getObjectByKey(planConfigObj, ALT_RES_POLICY.code));
        setEnableRollingPlan(getEnableRollingPlan(planConfigObj));
      }

      const reduxState = reduxUtil.createObjectState(viewName, { sourceVersion: selectedPlanVersion });
      reduxDispatch(reduxUtil.updateViewState(reduxState));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlanVersion]);

  useEffect(() => {
    setIsLoading(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoading) {

      configObj.current = makePlanConfigObj({
        planStartDate, planEndDate, bucketUnit, planningRange,
        salesPeriodRef, demandPeriodRef, dpVersion, rpVersion, kitDemandMergeFlg, noteFlg, multiSourcingFlg, initialDpVersion,
        backorder, frozenRange, planStrategy, materialConst, resourceCapacity, immediateShipment, useTargetSupplyDays, demandPriority,
        safetyStockPolicy, kitPlanMode, allowSemiOrder, altResPolicy, enableRollingPlan
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planStartDate, planEndDate, bucketUnit, planningRange, timeBuckets, salesPeriodRef, demandPeriodRef, demandPriority,
    dpVersion, rpVersion, kitDemandMergeFlg, noteFlg, multiSourcingFlg, demandDistributionPolicy, initialDpVersion, backorder, frozenRange,
    planStrategy, materialConst, resourceCapacity, immediateShipment, useTargetSupplyDays, safetyStockPolicy, kitPlanMode, allowSemiOrder,
    altResPolicy, enableRollingPlan]);

  useEffect(() => {
    if (initialDpVersion === null) {
      setInitialDpVersion(dpVersion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dpVersion]);

  useEffect(()=> {
    if ((isEqual(moduleCd, RP) || isEqual(moduleCd, MP)) && isStartWeekBucket && isLoading) {
      const startDate = getWeekStartDate(planStartDate);
      setPlanStartDate(startDate);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucketUnit, isStartWeekBucket]);


  useEffect(() => {
    if (!isEqual(moduleCd, MP)) return;
    const newRange = timeBuckets.reduce((s, tb) => s + (tb.COUNT || 0), 0);
    setPlanningRange(newRange);
    const firstUnit = timeBuckets[0]?.UNIT;
    if (firstUnit) setBucketUnit(firstUnit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeBuckets, moduleCd]);

  useEffect(() => {
    if (!planStartDate) {
      return;
    }

    const endDate = getCalculatedPlanEndDate(planStartDate, planningRange, bucketUnit, timeBuckets);
    if (endDate) {
      setPlanEndDate(endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleCd, planStartDate, planningRange, bucketUnit, timeBuckets]);

  useEffect(() => {
    if (!isEqual(moduleCd, MP)) {
      return;
    }

    if ((Number(frozenRange) || 0) > mpFirstBucketSize) {
      setFrozenRange(mpFirstBucketSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleCd, mpFirstBucketSize]);

  useEffect(()=> {
    const weekNum = dateUtils.getWeekOfYear(planStartDate);
    const planStartWeekLabel = weekNum < 10  ? `W0${weekNum}` : `W${weekNum}`;

    setPlanStartWeek(planStartWeekLabel);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planStartDate]);

  useEffect(()=> {
    const weekNum = dateUtils.getWeekOfYear(planEndDate);
    const planEndWeekLabel = weekNum < 10  ? `W0${weekNum}` : `W${weekNum}`;

    setPlanEndWeek(planEndWeekLabel);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planEndDate]);

  useEffect(() => {
    setRpVersion(null);
    if (dpVersion && rpVersions) {
      const childRpVersions = rpVersions.filter(version => version.parent?.id && isEqual(version.parent.id, dpVersion.id));
      if (childRpVersions) {
        setRpVersionsState(childRpVersions);
      } else {
        setRpVersionsState([]);
      }
    } else if(!isEmpty(useModules) && useModules.includes(ROLE_APP_RP) && !useModules.includes(ROLE_APP_DP)) {
      setRpVersionsState(rpVersions);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dpVersion]);

  useEffect(() => {
    if (rpVersionsState && rpVersionsState.length > 0) {
      const rpVersionIds = rpVersionsState.map(item => item?.id.toString());
      if (sourceRpVersion && rpVersionsState && rpVersionIds.includes(sourceRpVersion?.id.toString())) {
        setRpVersion(sourceRpVersion);
        setSourceRpVersion(null);
      } else {
        setRpVersion(rpVersionsState[0]);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpVersionsState]);

  const dpIcon = version => {
    const { statusCd } = version;

    if (isEqual(statusCd, RELEASED)) {
      return GavelIcon;
    }

    return ViewInArIcon;
  }

  const dpIconColor = version => {
    const { statusCd } = version;

    if (isEqual(statusCd, RELEASED)) {
      return grey[900];
    }

    return green[600];
  }

  const onChangeImmediateShipment = (event) => {
    const { target: { value } } = event;

    setImmediateShipment(value);
  };

  const setDpToolTip = (version) => {
    if (version?.planConfigObj) {
      const { PLAN_START_DATE: startDate, BUCKET_UNIT: unit, PLANNING_RANGE: range, START_DAY: startDay } = version.planConfigObj;
      let format = "yyyy-MM";
      if (isEqual(unit, WEEK)) {
        format = "yyyy-MM-dd";
      }
      return `${dateUtils.formatDate(startDate, format)} ~ ${dateUtils.formatDate(dateUtils.calculateRangeDate(new ZDate(startDate), unit, startDay, range, true), format)}`;
    }
    return null;
  }

  return (
    // fitContent: 모달이 내용만큼 커지도록 세로 스크롤 캡 해제(Create Version). 기본은 뷰포트 캡 유지(Version Settings).
    <Box sx={fitContent ? { overflowX: "hidden" } : { maxHeight: "calc(90vh - 150px)", overflowY: "auto", overflowX: "hidden" }}>
      <Box sx={{ width: "100%", boxSizing: "border-box" }}>
        {isEqual(moduleCd, MP) ? (
          <Box component={"fieldset"} sx={{ ...fieldSetStyle, width: "calc(100% - 2px)", boxSizing: "border-box" }}>
            <Box component={"legend"} mx={0.5}>{t("planningBucket")}</Box>
            <MpBucketStrategySettings
              t={t}
              timeBuckets={timeBuckets}
              setTimeBuckets={setTimeBuckets}
              compact={true}
              planStartDate={planStartDate}
              onPlanStartDateChange={(newDate) => {
                setPlanStartDate(newDate);
              }}
              onlyLastSegmentCount={true}
            />
          </Box>
        ) : (
          <Box component={"fieldset"} sx={{ ...fieldSetStyle, width: "calc(100% - 2px)", boxSizing: "border-box" }}>
            <Box component={"legend"} mx={0.5}>{t("planningBucket")}</Box>
            <FormControl size="small" key={"bucket"} sx={{ width: "100%" }}>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                value={bucketUnit}
                sx={{ ml: 1 }}
                onChange={onChangeBucketUnit}>
                <FormControlLabel value={DAY} control={<Radio />} label={t("daily")} />
                <FormControlLabel value={WEEK} control={<Radio />} label={t("week")} />
                <FormControlLabel value={MONTH} control={<Radio />} label={t("monthly")} />
              </RadioGroup>
            </FormControl>
          </Box>
        )}
      </Box>
      <Box sx={{ mt: 1, width: "100%", boxSizing: "border-box" }}>
        <Box component={"fieldset"} sx={{ ...fieldSetStyle, width: "calc(100% - 2px)", boxSizing: "border-box" }}>
          <Box component={"legend"} mx={0.5}>{t("planHorizon")}</Box>
          <Box sx={{ display: "flex", mt: 1, justifyContent: "space-between" }}>
            <TextField
              name="planningRange"
              label={t("planRange")}
              sx={{ width: isEqual(moduleCd, MP) ? "45%" : "100%" }}
              value={planningRange ?? ""}
              onChange={onChangePlanningRange}
              size="small"
              type="number"
              error={!isEqual(moduleCd, MP) && (isEqual(planningRange, "") || !isFinite(planningRange) || planningRange <= 0)}
              InputProps={{ inputProps: { min: 1, readOnly: isEqual(moduleCd, MP) }, inputMode: 'numeric', type: 'number' }}
              variant="outlined" />

            {isEqual(moduleCd, MP) && (
              <TextField
                label={t("frozenRange")}
                size="small"
                value={frozenRange ?? ""}
                name="frozenRange"
                type="number"
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*", min: 0, max: mpFirstBucketSize }}
                sx={{ ml: 2, width: "45%" }}
                onChange={onChangePlanningRange}
                variant="outlined"
              />
            )}
          </Box>
          <Box sx={{ display: "flex", mt: 2, justifyContent: "space-between" }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t("startDate")}
                views={isStartMonthBucket ? ['year', 'month'] : ['year', 'month', 'day']}
                format={isStartMonthBucket ? 'yyyy-MM' : isStartWeekBucket ? `yyyy-MM-dd ${planStartWeek}` : 'yyyy-MM-dd'}
                openTo={isStartMonthBucket ? 'month' : 'day'}
                size="small"
                value={planStartDate ?? null}
                onAccept={e => onChangePeriod(e, "planStartDate")}
                disabled={isEqual(moduleCd, MP)}
                sx={{ width: "45%" }}
                slotProps={{ textField: { InputProps: { size:"small" } } }}
              />
              <Box sx={{ width: "5%", p: 1 }}>
                <ArrowRightIcon />
              </Box>
              <DatePicker
                disabled
                label={t("endDate")}
                views={isEndMonthBucket ? ['year', 'month'] : ['year', 'month', 'day']}
                format={isEndMonthBucket ? 'yyyy-MM' : isEndWeekBucket ? `yyyy-MM-dd ${planEndWeek}` : 'yyyy-MM-dd'}
                openTo={isEndMonthBucket ? 'month' : 'day'}
                sx={{ width: "45%" }}
                size="small"
                value={planEndDate ?? null}
                onAccept={e => onChangePeriod(e, "planEndDate")}
                slotProps={{ textField: { InputProps: { size:"small" } } }}
              />
            </LocalizationProvider>
          </Box>

          {isEqual(moduleCd, MP) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1 }}>
              <StyledSwitch
                size="small"
                checked={enableRollingPlan}
                onChange={e => setEnableRollingPlan(e.target.checked)}
              />
              <Typography variant="subtitle1" sx={{ color: "text.primary" }}>
                {t("enableRollingPlan")}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      {
        isEqual(moduleCd, RP) && (
          <Box>
          <Box component={"fieldset"} sx={fieldSetStyle}>
            <Box component={"legend"} mx={0.5}>{t("demandDistributionPolicy")}</Box>
            <FormControl size="small" key={"bucket"} sx={{ width: "100%" }}>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                value={demandDistributionPolicy}
                sx={{ ml: 1 }}
                onChange={onChangeDistributionPolicy}>
                <FormControlLabel value={EQUAL_BY_BUCKET} control={<Radio />} label={t("equalByBucket")} />
                <FormControlLabel value={LAST_OF_BUCKET} control={<Radio />} label={t("lastOfBucket")} />
              </RadioGroup>
            </FormControl>
          </Box>
        </Box>
        )
      }
      <DpVersionDropDown
        moduleCd={moduleCd}
        dpVersions={dpVersions}
        dpVersion={dpVersion}
        useModules={useModules}
        setDpToolTip={setDpToolTip}
        onChangeSelectValueHandler={onChangeSelectValueHandler}
        dpIcon={dpIcon}
        dpIconColor={dpIconColor}
      />
      {isEqual(moduleCd, RP) && (
        <RpPolicyComponent
          planStartDate={planStartDate}
          salesPeriodRef={salesPeriodRef}
          demandPeriodRef={demandPeriodRef}
          kitDemandMergeFlg={kitDemandMergeFlg}
          useTargetSupplyDays={useTargetSupplyDays}
          noteFlg={noteFlg}
          multiSourcingFlg={multiSourcingFlg}
          setKitDemandMergeFlg={setKitDemandMergeFlg}
          setUseTargetSupplyDays={setUseTargetSupplyDays}
          setNoteFlg={setNoteFlg}
          setMultiSourcingFlg={setMultiSourcingFlg}
          rpPolicyContainer={rpPolicyContainer}
          backorder={backorder}
          immediateShipment={immediateShipment}
          handleChange={handleChange}
          onChangeImmediateShipment={onChangeImmediateShipment}
          onChangeSliderValueHandler={onChangeSliderValueHandler}
        />
      )}
      {isEqual(moduleCd, MP) && (
          <>
            <RpVersionDropDown
                rpVersion={rpVersion}
                rpVersionsState={rpVersionsState}
                useModules={useModules}
                onChangeSelectValueHandler={onChangeSelectValueHandler}
                setDpToolTip={setDpToolTip}
                dpIcon={dpIcon}
                dpIconColor={dpIconColor}
            />
            <MpPolicyComponent
                resourceCapacity={resourceCapacity}
                materialConst={materialConst}
                planStrategy={planStrategy}
                backorder={backorder}
                safetyStockPolicy={safetyStockPolicy}
                allowSemiOrder={allowSemiOrder}
                altResPolicy={altResPolicy}
                kitPlanMode={kitPlanMode}
                useModules={useModules}
                handleChange={handleChange}
                constraints={constraints}
                materialConstraints={materialConstraints}
                planScenarioItems={planScenarioItems}
                allowBackorder={allowBackorder}
                safetyStockPolicyOptions={safetyStockPolicyOptions}
                allowSemiOrderOptions={allowSemiOrderOptions}
                altResPolicyOptions={altResPolicyOptions}
                kitPlanModeOptions={kitPlanModeOptions}
                demandPriority={demandPriority}
                onReorderDemandPriority={handleReorderDemandPriority}
                planPolicyContainer={planPolicyContainer}
            />
          </>
      )}

    </Box>
  );
};

const MpPolicyComponent = ({
  resourceCapacity, materialConst, planStrategy, backorder, safetyStockPolicy,
  allowSemiOrder, altResPolicy, kitPlanMode, useModules, handleChange,
  constraints, materialConstraints, planScenarioItems, allowBackorder,
  safetyStockPolicyOptions, allowSemiOrderOptions, altResPolicyOptions, kitPlanModeOptions,
  demandPriority, onReorderDemandPriority,
  planPolicyContainer,
}) => {
  const { t } = useTranslation();
  // 포워드 계열(forward / forward2) — 드롭다운에는 포워드로 보이고, 둘의 구분은 "계획 기준" 선택 칸이 나타낸다.
  const isForward = planStrategy === planStrategies.forWard || planStrategy === planStrategies.forWard2;

  // planPolicyContainer가 null → ref 아직 준비 중 → 렌더링 보류 (인라인→포털 재마운트 방지)
  // planPolicyContainer가 undefined → 포털 컨테이너 미제공 → 렌더링하지 않음
  if (!planPolicyContainer) return null;

  const planPolicyJsx = (
    <Box sx={{ width: "100%", overflowY: "auto", overflowX: "hidden", boxSizing: "border-box", p: 0 }}>
      <Box sx={{ width: "100%", boxSizing: "border-box", p: 0 }}>
        <Box component={"fieldset"} sx={{ ...fieldSetStyle, width: "calc(100% - 2px)", boxSizing: "border-box" }}>
          <Box component={"legend"}>{t("planPolicy")}</Box>
          <Box sx={{ display: "inline-flex", width: "100%", gap: 2, mt: 0.5, ml: 1, alignItems: "center" }}>
            <Typography variant="subtitle1" sx={MP_POLICY_LABEL_SX}>{t("resourceCapa")}</Typography>
            <FormControl variant="standard" sx={{ width: MP_POLICY_CONTROL_WIDTH }} size="small">
              <Select variant="standard" id="resource-constraint" name="resourceCapacity" value={resourceCapacity ?? ""} onChange={handleChange}>
                {constraints?.map((el) => <MenuItem key={el.code} value={el.name}>{t(el.name)}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "inline-flex", width: "100%", gap: 2, mt: 2, ml: 1 }}>
            <Typography variant="subtitle1" sx={MP_POLICY_LABEL_SX}>{t(MC.name)}</Typography>
            <FormControl variant="standard" sx={{ width: MP_POLICY_CONTROL_WIDTH }} size="small">
              <Select variant="standard" id="material-constraint" name={MC.name} value={materialConst ?? ""} onChange={handleChange}>
                {materialConstraints?.map((el) => <MenuItem key={el.code} value={el.name}>{t(el.name)}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", width: "100%", gap: 2, mt: 2, ml: 1 }}>
            <Box sx={{ ...MP_POLICY_LABEL_SX, display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="subtitle1">{t(PS.name)}</Typography>
              <PlanStrategyHelp />
            </Box>
            <FormControl variant="standard" sx={{ width: MP_POLICY_CONTROL_WIDTH }} size="small">
              <Select variant="standard" id="plan-strategy" name={PS.name} value={isForward ? planStrategies.forWard : (planStrategy ?? "")} onChange={handleChange}>
                {planScenarioItems?.map((el) => <MenuItem key={el.code} value={el.name}>{t(el.name)}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          {isForward && (
            <Box sx={{ display: "inline-flex", width: "100%", gap: 2, mt: 2, ml: 1 }}>
              <Typography variant="subtitle1" sx={MP_POLICY_LABEL_SX}>{t("planningBasis")}</Typography>
              <FormControl variant="standard" sx={{ width: MP_POLICY_CONTROL_WIDTH }} size="small">
                <Select variant="standard" id="planning-basis" value={planStrategy} onChange={e => handleChange({ target: { name: PS.name, value: e.target.value } })}>
                  <MenuItem value={planStrategies.forWard}>{t("planningBasisEarliestAvailable")}</MenuItem>
                  <MenuItem value={planStrategies.forWard2}>{t("planningBasisDueDatePullIn")}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
          <Box sx={{ display: "inline-flex", width: "100%", gap: 2, mt: 2, ml: 1 }}>
            <Typography variant="subtitle1" sx={MP_POLICY_LABEL_SX}>{t(BACKORDER.name)}</Typography>
            <FormControl variant="standard" sx={{ width: MP_POLICY_CONTROL_WIDTH }} size="small">
              <Select variant="standard" id={BACKORDER.name} name={BACKORDER.name} value={backorder ?? ""} onChange={handleChange}>
                {allowBackorder?.map((el) => <MenuItem key={el.code} value={el.name}>{t(el.name)}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "inline-flex", width: "100%", gap: 2, mt: 2, ml: 1 }}>
            <Typography variant="subtitle1" sx={MP_POLICY_LABEL_SX}>{t(SAFETY_STOCK_POLICY.name)}</Typography>
            <FormControl variant="standard" sx={{ width: MP_POLICY_CONTROL_WIDTH }} size="small">
              <Select variant="standard" id={SAFETY_STOCK_POLICY.name} name={SAFETY_STOCK_POLICY.name} value={safetyStockPolicy ?? "NONE"} onChange={handleChange}>
                {safetyStockPolicyOptions?.map((el) => <MenuItem key={el.key} value={el.code}>{t(el.name)}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "inline-flex", width: "100%", gap: 2, mt: 2, ml: 1 }}>
            <Typography variant="subtitle1" sx={MP_POLICY_LABEL_SX}>{t(ALLOW_SEMI_ORDER.name)}</Typography>
            <FormControl variant="standard" sx={{ width: MP_POLICY_CONTROL_WIDTH }} size="small" disabled={kitPlanMode}>
              <Select variant="standard" id={ALLOW_SEMI_ORDER.name} name={ALLOW_SEMI_ORDER.name} value={allowSemiOrder ?? false} onChange={handleChange}>
                {allowSemiOrderOptions?.map((el) => <MenuItem key={el.key} value={el.code}>{t(el.name)}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "inline-flex", width: "100%", gap: 2, mt: 2, ml: 1 }}>
            <Typography variant="subtitle1" sx={MP_POLICY_LABEL_SX}>{t(ALT_RES_POLICY.name)}</Typography>
            <FormControl variant="standard" sx={{ width: MP_POLICY_CONTROL_WIDTH }} size="small">
              <Select variant="standard" id={ALT_RES_POLICY.name} name={ALT_RES_POLICY.name} value={altResPolicy ?? PRODUCTION_PRIORITY.FAST} onChange={handleChange}>
                {altResPolicyOptions?.map((el) => <MenuItem key={el.key} value={el.code}>{t(el.name)}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          {useModules?.includes(ROLE_APP_RP) && (
            <Box sx={{ display: "inline-flex", width: "100%", gap: 2, mt: 2, ml: 1 }}>
              <Typography variant="subtitle1" sx={MP_POLICY_LABEL_SX}>{t(KIT_PLAN_MODE.name)}</Typography>
              <FormControl variant="standard" sx={{ width: MP_POLICY_CONTROL_WIDTH }} size="small">
                <Select variant="standard" id={KIT_PLAN_MODE.name} name={KIT_PLAN_MODE.name} value={kitPlanMode ?? false} onChange={handleChange}>
                  {kitPlanModeOptions?.map((el) => <MenuItem key={el.key} value={el.code}>{t(el.name)}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={{ mt: 1, width: "100%", boxSizing: "border-box", p: 0 }}>
        <Box component={"fieldset"} sx={{ ...fieldSetStyle, width: "calc(100% - 2px)", boxSizing: "border-box" }}>
          <Box component={"legend"} mx={0.5}>{t("demandPriority")}</Box>
          <Box sx={{ width: "100%", boxSizing: "border-box", p: 0 }}>
            <Box sx={{ width: "100%", maxHeight: 150, overflowY: "auto", p: 0.25 }}>
              <DemandPriorityList
                items={demandPriority ?? []}
                keyField="priorityItemName"
                onReorder={onReorderDemandPriority}
                lockedIndex={(demandPriority ?? []).findIndex(row => includes(values(datePriorityNameByCode), row?.priorityItemName))}
                t={t}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return createPortal(planPolicyJsx, planPolicyContainer);
};

const DpVersionDropDown = ({moduleCd, dpVersions, dpVersion, useModules, setDpToolTip, onChangeSelectValueHandler, dpIcon, dpIconColor }) => {
  const { t } = useTranslation();

  if (isEqual(moduleCd, MP)) {
    const useDpModule = !isEmpty(useModules) ? useModules.includes(ROLE_APP_DP) : false;

    if (useDpModule) {
      return (
        <Box sx={{ mt: 1, width: "100%", boxSizing: "border-box" }}>
            <Box component={"fieldset"} sx={{ ...fieldSetStyle, width: "calc(100% - 2px)", boxSizing: "border-box" }}>
              <Box component={"legend"} mx={0.5}>{`${t("dp")} ${t("version")}`}</Box>
              <Box id="ver-config-content-dp-ver">
                <FormControl size="small" sx={{ mt: 0.5, width: "100%" }} key={"dpVersion"}>
                  <VersionAutocomplete
                    publishToChat={false}
                    options={dpVersions}
                    value={!isNull(dpVersion) ? dpVersion : null}
                    onChange={(_, newValue) => onChangeSelectValueHandler(newValue, DP)}
                    label={t("version")}
                    getIcon={dpIcon}
                    getIconColor={dpIconColor}
                    getTooltip={setDpToolTip}
                    showInputIcon={false}
                    sx={{ mr: 0, minWidth: 0, width: "100%" }}
                  />
                </FormControl>
              </Box>
            </Box>
          </Box>
      )
    } else {
      return "";
    }
  }

  return (
    <Box sx={{ mt: 1, width: "100%", boxSizing: "border-box" }}>
        <Box component={"fieldset"} sx={{ ...fieldSetStyle, width: "calc(100% - 2px)", boxSizing: "border-box" }}>
          <Box component={"legend"} mx={0.5}>{`${t("dp")} ${t("version")}`}</Box>
          <Box id="ver-config-content-dp-ver">
            <FormControl size="small" sx={{ mt: 0.5, width: "100%" }} key={"dpVersion"}>
              <VersionAutocomplete
                publishToChat={false}
                options={dpVersions}
                value={!isNull(dpVersion) ? dpVersion : null}
                onChange={(_, newValue) => onChangeSelectValueHandler(newValue, DP)}
                label={t("version")}
                getIcon={dpIcon}
                getIconColor={dpIconColor}
                getTooltip={setDpToolTip}
                showInputIcon={false}
                sx={{ mr: 0, minWidth: 0, width: "100%" }}
              />
            </FormControl>
          </Box>
        </Box>
      </Box>
  )
};

const RpPolicyPanel = ({ t, kitDemandMergeFlg, useTargetSupplyDays, noteFlg, multiSourcingFlg, backorder, immediateShipment, handleChange, onChangeImmediateShipment, setKitDemandMergeFlg, setUseTargetSupplyDays, setNoteFlg, setMultiSourcingFlg }) => {
  const handleCheckbox = (e, name) => {
    const value = e.target.checked;
    if (name === "noteFlg") setNoteFlg(value);
    else if (name === "kitSalesDemandMergeFlg") setKitDemandMergeFlg(value);
    else if (name === "useTargetSupplyDays") setUseTargetSupplyDays(value);
    else if (name === "multiSourcingFlg") setMultiSourcingFlg(value);
  };

  return (
    <Box sx={{ width: "100%", overflowY: "auto", overflowX: "hidden", boxSizing: "border-box", p: 0 }}>
      <Box sx={{ width: "100%", boxSizing: "border-box", p: 0 }}>
        <Box component={"fieldset"} sx={{ ...fieldSetStyle, width: "calc(100% - 2px)", boxSizing: "border-box" }}>
          <Box component={"legend"} mx={0.5}>{t("planPolicy")}</Box>
          <Box sx={{ display: "flex", alignItems: "center", width: "100%", mt: 1, px: 1, boxSizing: "border-box", minWidth: 0 }}>
            <Typography variant="subtitle1" sx={RP_POLICY_LABEL_SX}>{t(BACKORDER.name)}</Typography>
            <FormControl variant="standard" sx={RP_POLICY_CONTROL_SX} size="small">
              <Select variant="standard" id={BACKORDER.name} name={BACKORDER.name} value={backorder ?? ""} onChange={handleChange}>
                {allowBackorder?.map((el) => <MenuItem key={el.code} value={el.name}>{t(el.name)}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", width: "100%", mt: 1, px: 1, boxSizing: "border-box", minWidth: 0 }}>
            <Typography variant="subtitle1" sx={RP_POLICY_LABEL_SX}>{t("shipmentTime")}</Typography>
            <FormControl variant="standard" sx={RP_POLICY_CONTROL_SX} size="small">
              <Select variant="standard" id="immediateShipment" name="immediateShipment" value={immediateShipment ?? ""} onChange={onChangeImmediateShipment}>
                {allowImmediateShipment?.map((el) => <MenuItem key={el.code} value={el.name}>{t(el.name)}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", mt: 2, px: 1, pb: 1 }}>
            <Box component="label" key="KIT_DEMAND_MERGED_FLG" sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.5, cursor: "pointer" }}>
              <StyledSwitch size="small" name="kitSalesDemandMergeFlg" checked={kitDemandMergeFlg} onChange={(e) => handleCheckbox(e, "kitSalesDemandMergeFlg")} />
              <Typography variant="body2">{t("kitSalesDemandMergeFlg")}</Typography>
            </Box>
            <Box component="label" key="USE_TARGET_SUPPLY_DAYS" sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.5, cursor: "pointer" }}>
              <StyledSwitch size="small" name="useTargetSupplyDays" checked={useTargetSupplyDays} onChange={(e) => handleCheckbox(e, "useTargetSupplyDays")} />
              <Typography variant="body2">{t("useTargetSupplyDays")}</Typography>
            </Box>
            <Box component="label" key="NOTE_FLG" sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.5, cursor: "pointer" }}>
              <StyledSwitch size="small" name="noteFlg" checked={noteFlg} onChange={e => handleCheckbox(e, "noteFlg")} />
              <Typography variant="body2">{t("noteCopy")}</Typography>
            </Box>
            <Box component="label" key="MULTI_SOURCING_FLG" sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.5, cursor: "pointer" }}>
              <StyledSwitch size="small" name="multiSourcingFlg" checked={multiSourcingFlg} onChange={e => handleCheckbox(e, "multiSourcingFlg")} />
              <Typography variant="body2">{t("useMultiSourcing")}</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const RpVersionDropDown = ({ rpVersion, rpVersionsState, useModules, onChangeSelectValueHandler, setDpToolTip, dpIcon, dpIconColor }) => {
  const { t } = useTranslation();

  const useRpModule = !isEmpty(useModules) ? useModules.includes(ROLE_APP_RP) : false;
  if (!useRpModule) return "";

  const setRpToolTip = (version) => {
    if (version?.planConfigObj) {
      const { PLAN_START_DATE, PLAN_END_DATE } = version.planConfigObj;
      return `${PLAN_START_DATE} ~ ${PLAN_END_DATE}`;
    }
    return null;
  };

  return (
    <Box sx={{ mt: 1, width: "100%", boxSizing: "border-box" }}>
      <Box component={"fieldset"} sx={{ ...fieldSetStyle, width: "calc(100% - 2px)", boxSizing: "border-box" }}>
        <Box component={"legend"} mx={0.5}>{`${t("rp")} ${t("version")}`}</Box>
        <FormControl size="small" sx={{ mt: 0.5, width: "100%" }} key={"rpVersion"}>
          <VersionAutocomplete
            publishToChat={false}
            options={rpVersionsState}
            value={rpVersion}
            onChange={(_, newValue) => onChangeSelectValueHandler(newValue, RP)}
            label={t("version")}
            getIcon={dpIcon}
            getIconColor={dpIconColor}
            getTooltip={setRpToolTip}
            showInputIcon={false}
            sx={{ mr: 0, minWidth: 0, width: "100%" }}
          />
        </FormControl>
      </Box>
    </Box>
  );
};

const RpPolicyComponent = ({
  planStartDate,
  salesPeriodRef,
  demandPeriodRef,
  kitDemandMergeFlg,
  useTargetSupplyDays,
  noteFlg,
  multiSourcingFlg,
  setKitDemandMergeFlg,
  setUseTargetSupplyDays,
  setNoteFlg,
  setMultiSourcingFlg,
  rpPolicyContainer,
  backorder,
  immediateShipment,
  handleChange,
  onChangeImmediateShipment,
  onChangeSliderValueHandler,
}) => {
  const { t } = useTranslation();

  // rpPolicyContainer가 null → ref 아직 준비 중, undefined → 미제공 → 렌더링 보류
  if (!rpPolicyContainer) return null;
  const panelJsx = (
    <RpPolicyPanel
      t={t}
      kitDemandMergeFlg={kitDemandMergeFlg}
      useTargetSupplyDays={useTargetSupplyDays}
      noteFlg={noteFlg}
      multiSourcingFlg={multiSourcingFlg}
      backorder={backorder}
      immediateShipment={immediateShipment}
      handleChange={handleChange}
      onChangeImmediateShipment={onChangeImmediateShipment}
      setKitDemandMergeFlg={setKitDemandMergeFlg}
      setUseTargetSupplyDays={setUseTargetSupplyDays}
      setNoteFlg={setNoteFlg}
      setMultiSourcingFlg={setMultiSourcingFlg}
    />
  );

  return (
    <>
      <Box sx={{ mt: 1 }}>
        <RpPeriodSlider planStartDate={planStartDate} salesPeriodRef={salesPeriodRef} demandPeriodRef={demandPeriodRef} onChangeSliderValueHandler={onChangeSliderValueHandler} />
      </Box>
      {createPortal(panelJsx, rpPolicyContainer)}
    </>
  );
};

export default withTranslation()(VersionConfigContent);
